import { Component, DestroyRef, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';

export interface CodeVerificationData {
  key: string;
  value: string;
}

export interface CodeVerificationConfig {
  sessionId: string;
  otpValidityInMinute: number;
  transactionId: string;
  globalFeatureCode: string;
  identifierValue: string;
  userIdentity: string;
}

export interface CodeVerificationResult {
  passcode: string;
  trnUserCode: string;
  trnPassUserCode: string;
  sessionId: string;
  transactionId: string;
  globalFeatureCode: string;
  identifierValue: string;
  userIdentity: string;
}

@Component({
  selector: 'app-code-verification-dialog',
  templateUrl: './code-verification-dialog.component.html',
  styleUrl: './code-verification-dialog.component.scss',
})
export class CodeVerificationDialogComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Input() data: CodeVerificationData[] = [];
  @Input() config: CodeVerificationConfig | null = null;
  @Input() isLoading: boolean = false;
  @Input() hideTimer: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirm = new EventEmitter<CodeVerificationResult>();

  verificationCode: string = '';
  trnUserCode: string = '';
  trnPassUserCode: string = '';
  showTrnPassword: boolean = false;
  credentialInputsReadonly: boolean = true;
  timerDisplay: string = '00:00';
  isExpired: boolean = false;
  readonly verificationCodeLength = 6;
  private remainingSeconds: number = 0;
  private timerSubscription: any;

  constructor(private readonly destroyRef: DestroyRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible && this.config) {
      this.startTimer();
    }
    if (changes['visible'] && !this.visible) {
      this.stopTimer();
      this.resetState();
    }
  }

  private startTimer(): void {
    this.stopTimer();
    this.isExpired = false;
    this.remainingSeconds = (this.config?.otpValidityInMinute || 0) * 60;
    this.updateTimerDisplay();

    this.timerSubscription = interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.remainingSeconds > 0) {
          this.remainingSeconds--;
          this.updateTimerDisplay();
        } else {
          this.isExpired = true;
          this.stopTimer();
        }
      });
  }

  private stopTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }

  private updateTimerDisplay(): void {
    const minutes = Math.floor(this.remainingSeconds / 60);
    const seconds = this.remainingSeconds % 60;
    this.timerDisplay = `${this.padZero(minutes)}:${this.padZero(seconds)}`;
  }

  private padZero(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }

  private resetState(): void {
    this.verificationCode = '';
    this.trnUserCode = '';
    this.trnPassUserCode = '';
    this.showTrnPassword = false;
    this.credentialInputsReadonly = true;
    this.timerDisplay = '00:00';
    this.isExpired = false;
    this.remainingSeconds = 0;
  }

  enableCredentialInputs(): void {
    this.credentialInputsReadonly = false;
  }

  toggleTrnPasswordVisibility(): void {
    this.showTrnPassword = !this.showTrnPassword;
  }

  onKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    // Allow only numbers (0-9)
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  onVerificationCodeChange(value: string | number): void {
    this.verificationCode = `${value || ''}`.replace(/\D/g, '').slice(0, this.verificationCodeLength);
  }

  canConfirm(): boolean {
    return (
      this.verificationCode.length === this.verificationCodeLength &&
      !!this.trnUserCode?.trim() &&
      !!this.trnPassUserCode &&
      !this.isExpired &&
      !this.isLoading
    );
  }

  onConfirm(): void {
    if (!this.canConfirm() || !this.config) return;

    const result: CodeVerificationResult = {
      passcode: this.verificationCode,
      trnUserCode: this.trnUserCode.trim(),
      trnPassUserCode: this.trnPassUserCode,
      sessionId: this.config.sessionId,
      transactionId: this.config.transactionId,
      globalFeatureCode: this.config.globalFeatureCode,
      identifierValue: this.config.identifierValue,
      userIdentity: this.config.userIdentity,
    };
    this.confirm.emit(result);
  }

  closeDialog(): void {
    this.stopTimer();
    this.resetState();
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
